import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TokenPort } from '@core/ports/auth.port';

const ACCESS_TOKEN_KEY = 'cms_access_token';
const REFRESH_TOKEN_KEY = 'cms_refresh_token';
const USER_KEY = 'cms_user';

/**
 * SSR note (S7 Fase B divergence, recorded for the seed audit): the chassis
 * service assumed a browser; under SSR there is no `localStorage`, so every
 * read returns `null` and every write is a no-op on the server. The storefront
 * renders as an anonymous visitor and the admin/auth surfaces stay CSR.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenService implements TokenPort {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  getAccessToken(): string | null {
    return this.isBrowser ? localStorage.getItem(ACCESS_TOKEN_KEY) : null;
  }

  setAccessToken(token: string): void {
    if (!this.isBrowser) return;
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return this.isBrowser ? localStorage.getItem(REFRESH_TOKEN_KEY) : null;
  }

  setRefreshToken(token: string): void {
    if (!this.isBrowser) return;
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  clearTokens(): void {
    if (!this.isBrowser) return;
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  decodeToken(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded || typeof decoded['exp'] !== 'number') {
      return true;
    }
    return new Date(decoded['exp'] * 1000) <= new Date();
  }

  /**
   * Extracts the `organizationId` claim from a JWT access token.
   *
   * Reuses {@link decodeToken} (base64url-safe) so this works for tokens
   * issued by the BFF in any environment. Returns `null` when:
   * - the token is malformed (decode fails)
   * - the payload exists but lacks an `organizationId` claim
   * - the claim exists but is not a string
   *
   * Used by `AuthService.currentOrganizationId` (Path C — JWT decode).
   * See ADR-AI-17.
   */
  extractOrgIdFromToken(token: string): string | null {
    const decoded = this.decodeToken(token);
    if (!decoded) return null;
    const orgId = decoded['organizationId'];
    return typeof orgId === 'string' ? orgId : null;
  }

  getSavedUser(): Record<string, unknown> | null {
    if (!this.isBrowser) return null;
    const data = localStorage.getItem(USER_KEY);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  saveUser(user: Record<string, unknown>): void {
    if (!this.isBrowser) return;
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
}
